import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, FormControl, FormLabel, RadioGroup,
    FormControlLabel, Radio, Box, Select,
    MenuItem, InputLabel, CircularProgress
} from '@mui/material';
import SearchBox from '../SearchBox';
import { executeFetchAsync } from '../../Util';
import { config, QuizSubject } from '../../Const';

const CACHE_KEY = 'quiz_creation_last_entity';

function QuizCreationDialog({ open, onClose, onSelect }) {
    const [entityType, setEntityType] = useState('Musical Entity');

    // 1. Load cached entity on initialization
    const [selectedEntity, setSelectedEntity] = useState(() => {
        const cached = localStorage.getItem(CACHE_KEY);
        return cached ? JSON.parse(cached) : null;
    });

    // 2. Default subject to GENERAL
    const [selectedSubject, setSelectedSubject] = useState(QuizSubject.GENERAL);

    const [subItems, setSubItems] = useState([]);
    const [selectedSubItem, setSelectedSubItem] = useState(null);
    const [loadingSubItems, setLoadingSubItems] = useState(false);

    // Persist entity to localStorage when it changes
    useEffect(() => {
        if (selectedEntity) {
            localStorage.setItem(CACHE_KEY, JSON.stringify(selectedEntity));
        }
    }, [selectedEntity]);

    const handleClose = () => {
        setEntityType('Musical Entity');
        // We do NOT clear selectedEntity so it persists for the next open
        setSelectedSubject(QuizSubject.GENERAL);
        setSubItems([]);
        setSelectedSubItem(null);
        onClose();
    };

    const handleConfirm = () => {
        onSelect({
            entityType,
            entity: selectedEntity,
            subject: selectedSubject,
            subItem: selectedSubItem
        });
        handleClose();
    };

    useEffect(() => {
        const controller = new AbortController();

        const fetchSubItems = async () => {
            const isSpecific = [QuizSubject.SPECIFIC_ALBUM, QuizSubject.SPECIFIC_SONG].includes(selectedSubject);

            if (!selectedEntity?.id || !isSpecific) {
                setSubItems([]);
                return;
            }

            setLoadingSubItems(true);
            const endpoint = selectedSubject === QuizSubject.SPECIFIC_ALBUM ? 'albums' : 'songs';
            const base = config.SERVER_URL;
            const url = `${base}/api/v1/admin/entityData/${selectedEntity.id}/${endpoint}`;

            try {
                const data = await executeFetchAsync(url, "get", null, null, controller.signal);

                if (data) {
                    const sortedData = [...data].sort((a, b) => {
                        if (selectedSubject === QuizSubject.SPECIFIC_ALBUM) {
                            return (b.playCount || 0) - (a.playCount || 0);
                        }
                        return (b.listeners || 0) - (a.listeners || 0);
                    });
                    setSubItems(sortedData);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error(`Error fetching ${endpoint}:`, error.message);
                }
            } finally {
                setLoadingSubItems(false);
            }
        };

        fetchSubItems();

        return () => controller.abort();
    }, [selectedSubject, selectedEntity]);

    const isSpecificSubject = [QuizSubject.SPECIFIC_ALBUM, QuizSubject.SPECIFIC_SONG].includes(selectedSubject);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>New Multiple Choice Quiz</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

                    <FormControl>
                        <FormLabel>Entity Category</FormLabel>
                        <RadioGroup row value={entityType} onChange={(e) => setEntityType(e.target.value)}>
                            <FormControlLabel value="Musical Entity" control={<Radio />} label="Musical Entity" />
                            <FormControlLabel value="Other" control={<Radio />} label="Other" />
                        </RadioGroup>
                    </FormControl>

                    {entityType === 'Musical Entity' && (
                        <>
                            <SearchBox
                                value={selectedEntity}
                                endPoint={'entity'}
                                onEntitySelected={(e) => {
                                    setSelectedEntity(e);
                                    setSelectedSubject(QuizSubject.GENERAL);
                                    setSelectedSubItem(null);
                                }}
                                resultMappingFunction={(r) => ({
                                    label: r.label,
                                    id: r.idMapping?.WIKIDATA ?? r.id ?? r.wikidataId,
                                    type: r.type,
                                    seoTitle: r.seoTitle,
                                })}
                            />

                            {selectedEntity && (
                                <FormControl fullWidth>
                                    <InputLabel id="subject-select-label">Select Quiz Subject</InputLabel>
                                    <Select
                                        labelId="subject-select-label"
                                        value={selectedSubject}
                                        label="Select Quiz Subject"
                                        onChange={(e) => {
                                            setSelectedSubject(e.target.value);
                                            setSelectedSubItem(null);
                                        }}
                                    >
                                        {Object.values(QuizSubject).map((opt) => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {isSpecificSubject && (
                                <FormControl fullWidth sx={{ mt: 1 }}>
                                    <InputLabel id="subitem-select-label">
                                        {selectedSubject === QuizSubject.SPECIFIC_ALBUM ? 'Select Album' : 'Select Song'}
                                    </InputLabel>
                                    <Select
                                        labelId="subitem-select-label"
                                        value={selectedSubItem || ''}
                                        label={selectedSubject === QuizSubject.SPECIFIC_ALBUM ? 'Select Album' : 'Select Song'}
                                        onChange={(e) => setSelectedSubItem(e.target.value)}
                                        disabled={loadingSubItems}
                                    >
                                        {loadingSubItems ? (
                                            <MenuItem disabled>
                                                <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
                                            </MenuItem>
                                        ) : (
                                            subItems.map((item) => (
                                                <MenuItem key={item.id} value={item}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                        <Box>
                                                            {item.label} <strong>({item.releaseDate?.year || 'N/A'})</strong>
                                                        </Box>
                                                        <Box sx={{ ml: 2, color: 'text.secondary', fontSize: '0.85rem' }}>
                                                            {selectedSubject === QuizSubject.SPECIFIC_ALBUM
                                                                ? `${item.playCount?.toLocaleString()} plays`
                                                                : `${item.listeners?.toLocaleString()} listeners`}
                                                        </Box>
                                                    </Box>
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={
                        (entityType === 'Musical Entity' && (!selectedEntity || !selectedSubject)) ||
                        (isSpecificSubject && !selectedSubItem)
                    }
                >
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default QuizCreationDialog;